import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Beer, UserPlus, User, Heart, MapPin, Clock, CalendarCheck, Menu, X, LogIn, LogOut, Building2, Calendar, PlusCircle, MessageCircle, Globe, Users, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import PremiumBadge from './PremiumBadge';

const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [hasVenue, setHasVenue] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second

  useEffect(() => {
    // Check if screen is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Get initial user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user) {
        checkVenues(user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user;
      setUser(currentUser ?? null);
      if (currentUser) {
        checkVenues(currentUser.id);
      } else {
        setHasVenue(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkVenues = async (userId: string) => {
    try {
      // Check for restaurant
      const { data: venues, error: venuesError } = await supabase
        .from('restaurants')
        .select('id')
        .eq('owner_id', userId)
        .or('status.eq.approved,status.eq.pending')
        .limit(1);

      if (venuesError) {
        // If it's a network error and we haven't exceeded max retries, retry
        if (retryCount < maxRetries) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            checkVenues(userId);
          }, retryDelay * (retryCount + 1)); // Exponential backoff
          return;
        }
        throw venuesError;
      }

      if (!venues?.length) {
        // If no restaurant found, check for bar
        const { data: bars, error: barsError } = await supabase
          .from('bars')
          .select('id')
          .eq('owner_id', userId)
          .or('status.eq.approved,status.eq.pending')
          .limit(1);

        if (barsError) {
          // If it's a network error and we haven't exceeded max retries, retry
          if (retryCount < maxRetries) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              checkVenues(userId);
            }, retryDelay * (retryCount + 1)); // Exponential backoff
            return;
          }
          throw barsError;
        }
        setHasVenue(!!bars?.length);
      } else {
        setHasVenue(true);
      }

      // Reset retry count on success
      setRetryCount(0);
    } catch (err) {
      console.error('Error checking venues:', err);
      // Error is handled silently - we just set hasVenue to false
      setHasVenue(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      // Navigate to login page after sign out
      navigate('/login');
      // Close the menu
      setIsMenuOpen(false);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const menuItems = [
    { to: "/nearby-listings", icon: Users, label: t('nav.nearbyListings') },
    { to: "/nearby", icon: MapPin, label: t('nav.nearby') },
    { to: "/blind-dates", icon: Heart, label: t('nav.blindDates') },
    { to: "/my-dinners", icon: Beer, label: t('nav.myDinners'), requiresAuth: true },
    { to: "/my-venues", icon: Building2, label: t('nav.myVenues'), showIfHasVenue: true },
    { to: "/my-events", icon: Calendar, label: t('nav.myEvents'), showIfHasVenue: true },
    { to: "/my-reservations", icon: CalendarCheck, label: t('nav.myReservations'), showIfHasVenue: true },
    { to: "/reservations", icon: CalendarCheck, label: t('nav.reservations') },
    { to: "/restaurant-signup", icon: UserPlus, label: t('nav.registerVenue'), hideIfHasVenue: true },
    { to: "/premium", icon: Crown, label: "Premium" },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (item.hideIfHasVenue && hasVenue) return false;
    if (item.showIfHasVenue && !hasVenue) return false;
    if (item.requiresAuth && !user) return false;
    return true;
  });

  return (
    <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Beer className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-gray-900">Meet&Eat</span>
            </Link>
          </div>
          
          {/* Menu Button - Always visible */}
          <div className="flex items-center">
            {user ? (
              <div className="flex items-center space-x-4">
                <Link 
                  to="/chat" 
                  className="p-2 text-gray-600 hover:text-orange-500 transition"
                >
                  <MessageCircle className="h-6 w-6" />
                </Link>
                {isMobile ? (
                  <Link 
                    to="/premium" 
                    className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-xl hover:bg-yellow-500 transition flex items-center space-x-2"
                  >
                    <Crown className="h-5 w-5" />
                    <span>Premium</span>
                    <PremiumBadge className="ml-1" showLabel={false} />
                  </Link>
                ) : (
                  <Link 
                    to="/profile" 
                    className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center space-x-2"
                  >
                    <User className="h-5 w-5" />
                    <span>{t('nav.profile')}</span>
                    <PremiumBadge className="ml-1" showLabel={false} />
                  </Link>
                )}
              </div>
            ) : (
              <Link 
                to="/register" 
                className="mr-4 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center space-x-2"
              >
                <UserPlus className="h-5 w-5" />
                <span>{t('nav.signUp')}</span>
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-xl text-gray-700 hover:text-orange-500 focus:outline-none"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Menu Panel - Slide-in overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsMenuOpen(false)}
          ></div>
          
          {/* Menu Content */}
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-gradient-to-br from-orange-50 to-white shadow-xl rounded-l-3xl">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-l-3xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">{t('nav.menu')}</h2>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 rounded-xl text-white hover:bg-white/20 transition"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto py-6 px-4">
                <div className="space-y-2">
                  {filteredMenuItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-orange-100 hover:text-orange-600 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="text-lg">{item.label}</span>
                      {item.to === "/premium" && <PremiumBadge className="ml-1" showLabel={false} />}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200">
                {user ? (
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>{t('nav.signOut')}</span>
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <LogIn className="h-5 w-5" />
                    <span>{t('nav.signIn')}</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;